/**
 * Comment anchoring — PURE logic for F-50..F-53 (no React, no DOM, no Yjs).
 * docs/04 §3 is the spec:
 *
 *  - An anchor is a REFERENCE + OFFSET, resolved to a board position at
 *    render time from the same reactive state the board renders from —
 *    element `x`/`y`, or the current cell rect from the grid's own layout
 *    pass. Anchors are id-based, so pins follow row moves, sorts, resizes
 *    and element drags with NO event wiring (they cannot drift out of sync).
 *  - Dropping a pin hit-tests TOP-DOWN: floating element under the pointer →
 *    grid cell → bare board coordinate (fallback).
 *  - When a target no longer exists the anchor degrades to `board` at the
 *    last resolved position and the thread is flagged orphaned (docs/07 C4);
 *    `anchorTargetGone` is the detector, the caller performs the one-
 *    transaction degradation write.
 *
 * The cell math reuses core/table.ts (`cellAtPosition`) so comment hit-tests
 * agree exactly with the F-30 image-drop targeting.
 */
import type { Anchor } from "@kola/shared";

import { cellAtPosition } from "../../core/table.js";
import type { BoardRect, AddCommentTarget } from "../../registries/toolbar-actions/index.js";
import type { GridLayoutSnapshot } from "../board/GridTable.js";

/** A point in board units. */
export interface BoardPoint {
  x: number;
  y: number;
}

/** The subset of element state hit-testing/resolution needs (⊂ ElementInfo). */
export interface ElementRectLike {
  id: string;
  x: number;
  y: number;
  w: number;
  h: number;
}

/* ------------------------------------------------------------------ */
/* Cell rects from the grid's published layout                         */
/* ------------------------------------------------------------------ */

/**
 * Board-space rect of a cell in the grid's CURRENT layout, or null when the
 * row/column id is gone (or the layout hasn't been published). Edges are
 * wrapper-relative board units; the snapshot's origin lifts them to board
 * space.
 */
export function cellRectFromLayout(
  layout: GridLayoutSnapshot,
  rowId: string,
  colId: string,
): BoardRect | null {
  // O(1) via the published index maps, not a scan of `rowIds` — this runs per
  // pin per render, including on every awareness tick and every zoom frame.
  const r = layout.rowIndex.get(rowId);
  const c = layout.colIndex.get(colId);
  if (r === undefined || c === undefined) return null;
  const left = layout.colEdges[c];
  const right = layout.colEdges[c + 1];
  const top = layout.rowEdges[r];
  const bottom = layout.rowEdges[r + 1];
  if (left === undefined || right === undefined || top === undefined || bottom === undefined) {
    // Defensive. Since F-10 windowing, edges are DERIVED for every row in the
    // same pass that produces `rowIds` (they are no longer read back from the
    // DOM, which would only ever have the mounted rows), so an id without an
    // edge should not occur. If it somehow does, the cell is "not resolvable
    // THIS pass" — never a wrong rect, and never evidence the row is gone.
    return null;
  }
  return {
    x: layout.originX + left,
    y: layout.originY + top,
    w: right - left,
    h: bottom - top,
  };
}

/* ------------------------------------------------------------------ */
/* Drop hit-testing (F-50) — docs/04 §3 top-down order                 */
/* ------------------------------------------------------------------ */

/**
 * The target a pin at `point` would attach to, WITH the geometry needed to
 * highlight it (F-52b: "while dragging, the prospective target is highlighted
 * — element outline / cell tint / canvas ghost — so it's unambiguous what the
 * comment will belong to"). `anchor` is exactly what `hitTestCommentTarget`
 * returns; the extra fields exist so the drag preview and the eventual drop
 * can never disagree about what was hit — one resolution, one source of truth.
 */
export type PinDropTarget =
  | { kind: "element"; anchor: Anchor; elementId: string; rect: BoardRect }
  | { kind: "cell"; anchor: Anchor; rowId: string; colId: string; rect: BoardRect }
  | { kind: "board"; anchor: Anchor; point: BoardPoint };

/**
 * Resolve the drop target for a pin at `point` (board units) — the F-50 drop
 * and the F-52b re-anchor drag share this ONE hit-test.
 *
 * TOP-DOWN per docs/04 §3: the topmost floating element under the point wins
 * (elements render above the table), then the grid cell under it, then the
 * bare board coordinate. `elementsPaintOrder` is the render order — sorted by
 * (z, id) ascending, i.e. LAST element paints on top — exactly what
 * BoardDoc.listElements() returns.
 */
export function resolvePinDropTarget(
  point: BoardPoint,
  elementsPaintOrder: readonly ElementRectLike[],
  layout: GridLayoutSnapshot | null,
): PinDropTarget {
  for (let i = elementsPaintOrder.length - 1; i >= 0; i--) {
    const el = elementsPaintOrder[i];
    if (el === undefined) continue;
    if (point.x >= el.x && point.x < el.x + el.w && point.y >= el.y && point.y < el.y + el.h) {
      return {
        kind: "element",
        anchor: { kind: "element", elementId: el.id, dx: point.x - el.x, dy: point.y - el.y },
        elementId: el.id,
        rect: { x: el.x, y: el.y, w: el.w, h: el.h },
      };
    }
  }
  if (layout !== null) {
    const hit = cellAtPosition(
      layout.colEdges,
      layout.rowEdges,
      point.x - layout.originX,
      point.y - layout.originY,
    );
    if (hit !== null) {
      const rowId = layout.rowIds[hit.row];
      const colId = layout.colIds[hit.col];
      if (rowId !== undefined && colId !== undefined) {
        return {
          kind: "cell",
          anchor: {
            kind: "cell",
            rowId,
            colId,
            dx: point.x - (layout.originX + hit.rect.left),
            dy: point.y - (layout.originY + hit.rect.top),
          },
          rowId,
          colId,
          rect: {
            x: layout.originX + hit.rect.left,
            y: layout.originY + hit.rect.top,
            w: hit.rect.width,
            h: hit.rect.height,
          },
        };
      }
    }
  }
  return { kind: "board", anchor: { kind: "board", x: point.x, y: point.y }, point };
}

/**
 * Resolve the anchor for a pin dropped at `point` (board units) — the F-50
 * comment-mode drop, which needs the anchor and nothing else.
 */
export function hitTestCommentTarget(
  point: BoardPoint,
  elementsPaintOrder: readonly ElementRectLike[],
  layout: GridLayoutSnapshot | null,
): Anchor {
  return resolvePinDropTarget(point, elementsPaintOrder, layout).anchor;
}

/**
 * F-52b: does dropping the pin on `next` actually change the thread's target?
 * "Dropping on the current target is a no-op" — no write, no undo entry, no
 * sync op.
 *
 * Every kind honors its offset (2026-07-23: element pins used to compare by
 * target id ALONE and ignore `(dx, dy)` — they rode a fixed top-right badge
 * regardless of where they were dropped; now they sit wherever they were
 * dropped and just follow the element, so a same-element re-drop at a new
 * offset IS a real change, same as cell and board anchors).
 */
export function anchorDropChanged(current: Anchor, next: Anchor): boolean {
  if (next.kind === "element") {
    return (
      current.kind !== "element" ||
      current.elementId !== next.elementId ||
      current.dx !== next.dx ||
      current.dy !== next.dy
    );
  }
  if (next.kind === "cell") {
    return (
      current.kind !== "cell" ||
      current.rowId !== next.rowId ||
      current.colId !== next.colId ||
      current.dx !== next.dx ||
      current.dy !== next.dy
    );
  }
  return current.kind !== "board" || current.x !== next.x || current.y !== next.y;
}

/* ------------------------------------------------------------------ */
/* Render-time anchor resolution (F-52)                                */
/* ------------------------------------------------------------------ */

const clamp = (v: number, lo: number, hi: number): number => Math.min(hi, Math.max(lo, v));

/**
 * Resolve an anchor to the pin's board position for THIS render pass, or
 * null when it cannot be resolved right now:
 *
 *  - element target missing → null (candidate for C4 degradation); present →
 *    the element's CURRENT rect + the stored `(dx, dy)` offset, so the pin
 *    sits exactly where it was dropped and tracks the element's move/resize
 *    live (2026-07-23: previously ignored `(dx, dy)` and always rode a fixed
 *    top-right badge — see anchorDropChanged for the matching change);
 *  - cell target: null when the grid layout is unavailable (grid not mounted
 *    yet — NOT evidence the row is gone) or the row/column id vanished;
 *  - board anchors always resolve to themselves.
 *
 * Offsets are NOT clamped into the target's current rect: a pin nudged just
 * outside an element's edge (the old badge convention, or a drop near the
 * boundary) should stay exactly there, not get dragged back inside as the
 * element resizes. Cell anchors are the one exception (clamped below) — the
 * grid's gutter has no "just outside" concept the pin could rest in.
 */
export function resolveAnchorPosition(
  anchor: Anchor,
  getElementRect: (elementId: string) => BoardRect | null,
  layout: GridLayoutSnapshot | null,
): BoardPoint | null {
  switch (anchor.kind) {
    case "board":
      return { x: anchor.x, y: anchor.y };
    case "element": {
      const rect = getElementRect(anchor.elementId);
      if (rect === null) return null;
      return { x: rect.x + anchor.dx, y: rect.y + anchor.dy };
    }
    case "cell": {
      if (layout === null) return null;
      const rect = cellRectFromLayout(layout, anchor.rowId, anchor.colId);
      if (rect === null) return null;
      return { x: rect.x + clamp(anchor.dx, 0, rect.w), y: rect.y + clamp(anchor.dy, 0, rect.h) };
    }
  }
}

/**
 * True when the anchor's target POSITIVELY no longer exists — the C4
 * degradation trigger (reanchor to board + mark orphaned). Deliberately
 * conservative: a cell anchor with NO published grid layout returns false
 * (the grid simply isn't mounted; treating that as deletion would orphan
 * every cell thread on open). Board anchors have no target to lose.
 */
export function anchorTargetGone(
  anchor: Anchor,
  hasElement: (elementId: string) => boolean,
  layout: GridLayoutSnapshot | null,
): boolean {
  switch (anchor.kind) {
    case "board":
      return false;
    case "element":
      return !hasElement(anchor.elementId);
    case "cell":
      if (layout === null) return false;
      return !layout.rowIndex.has(anchor.rowId) || !layout.colIndex.has(anchor.colId);
  }
}

/**
 * True when the anchor's target POSITIVELY exists — the §3 re-attach trigger
 * (an orphaned thread whose original target id reappeared, i.e. the deletion
 * was undone). NOT the negation of `anchorTargetGone`: both detectors are
 * conservative in opposite directions — a cell anchor with NO published grid
 * layout is neither provably gone NOR provably present, so it must trigger
 * neither degradation nor re-attach. Board anchors have no target, so they
 * never trigger a re-attach.
 */
export function anchorTargetExists(
  anchor: Anchor,
  hasElement: (elementId: string) => boolean,
  layout: GridLayoutSnapshot | null,
): boolean {
  switch (anchor.kind) {
    case "board":
      return false;
    case "element":
      return hasElement(anchor.elementId);
    case "cell":
      return (
        layout !== null &&
        layout.rowIndex.has(anchor.rowId) &&
        layout.colIndex.has(anchor.colId)
      );
  }
}

/* ------------------------------------------------------------------ */
/* Comment-mode pin hit-testing (F-50/F-51)                            */
/* ------------------------------------------------------------------ */

/** A rendered pin's screen-space anchor (tip) point, in paint order. */
export interface PinScreenTip {
  threadId: string;
  /** boardToScreen projection of the pin's resolved board position. */
  tip: { x: number; y: number };
  /**
   * The pin's UNSCALED width, when it is wider than the base teardrop — a pin
   * carrying a facepile grows rightward from its tip (features/comments/
   * facepile.ts `pinWidthPx`). Absent falls back to `size`, which is what a
   * one-face pin is.
   */
  width?: number;
}

/**
 * The pin (topmost first — pins paint in list order, so LATER entries are on
 * top) whose screen box contains `point`, or null. The comment-mode capture
 * layer sits ABOVE the pins, so a click while in comment mode must be
 * hit-tested here to open the existing thread instead of dropping a new
 * draft on top of it (Miro behavior — F-50/F-51).
 *
 * Pin geometry mirrors the `.comment-pin` CSS: a `size`×`size` screen-px box
 * whose bottom-LEFT tip sits at the anchor point (margin-top: -size,
 * counter-scaled to constant screen size — hence screen px, not board units).
 */
export function pinAtScreenPoint(
  point: { x: number; y: number },
  pinsInPaintOrder: readonly PinScreenTip[],
  size: number,
): string | null {
  for (let i = pinsInPaintOrder.length - 1; i >= 0; i--) {
    const pin = pinsInPaintOrder[i];
    if (pin === undefined) continue;
    const { tip } = pin;
    // Width may exceed the height: the box is the painted pin, not a square.
    const width = pin.width ?? size;
    if (point.x >= tip.x && point.x <= tip.x + width && point.y >= tip.y - size && point.y <= tip.y) {
      return pin.threadId;
    }
  }
  return null;
}

/* ------------------------------------------------------------------ */
/* F-27 add-comment hook targets → anchors                             */
/* ------------------------------------------------------------------ */

/**
 * Anchor for a toolbar-initiated comment (F-27 add-comment): pinned to the
 * CENTER of the target's current rect — the offset is (w/2, h/2) so the pin
 * stays centered as the target moves/resizes (clamped at render time).
 */
export function anchorForTarget(target: AddCommentTarget): Anchor {
  if (target.kind === "cell") {
    return {
      kind: "cell",
      rowId: target.rowId,
      colId: target.colId,
      dx: target.rect.w / 2,
      dy: target.rect.h / 2,
    };
  }
  return {
    kind: "element",
    elementId: target.elementId,
    dx: target.rect.w / 2,
    dy: target.rect.h / 2,
  };
}

/**
 * Board point where the composer/draft pin for a toolbar target sits — the
 * target rect's center, for both kinds, matching `anchorForTarget`'s (w/2,
 * h/2) offset exactly, so the draft pin sits exactly where the created
 * thread's pin will.
 */
export function targetPinPosition(target: AddCommentTarget): BoardPoint {
  return { x: target.rect.x + target.rect.w / 2, y: target.rect.y + target.rect.h / 2 };
}

/* ------------------------------------------------------------------ */
/* Floating bubble placement (composer / thread popover)               */
/* ------------------------------------------------------------------ */

/**
 * The board rect of the thing an anchor points AT — the cell or element the
 * comment is about — or null for a bare-board anchor (nothing to keep clear)
 * or a target that cannot be resolved this pass. Same inputs as
 * `resolveAnchorPosition`, so the rect and the pin always agree.
 */
export function resolveAnchorTargetRect(
  anchor: Anchor,
  getElementRect: (elementId: string) => BoardRect | null,
  layout: GridLayoutSnapshot | null,
): BoardRect | null {
  switch (anchor.kind) {
    case "board":
      return null;
    case "element":
      return getElementRect(anchor.elementId);
    case "cell":
      return layout === null ? null : cellRectFromLayout(layout, anchor.rowId, anchor.colId);
  }
}

/** Container-relative screen rect (CSS px). */
export interface ScreenRect {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

export type BubbleSide = "above" | "right" | "left" | "below";

export interface BubblePlacement {
  side: BubbleSide;
  left: number;
  /**
   * The bubble's TOP edge — except on the `above` side, where it is the
   * BOTTOM edge: an above bubble is pinned by the edge that touches the pin,
   * so a thread that grows (a reply lands, the textarea wraps) extends upward
   * instead of sliding over the pin before the next measurement.
   */
  y: number;
}

export interface BubblePlacementInput {
  /** The painted pin box (tip = bottom-left corner). */
  pin: ScreenRect;
  /** The commented cell/element on screen, or null for a board anchor. */
  target: ScreenRect | null;
  bubble: { width: number; height: number };
  viewport: { width: number; height: number };
  /** Space between pin and bubble — the open pin's outline width. */
  gap: number;
  /** Preferred inset from the viewport edges when sliding along the pin. */
  margin: number;
}

/**
 * Candidate placements in preference order. Each puts one of the bubble's
 * CORNERS at the pin, its edge running along one side of the pin box:
 * above/below — `start` = the corner at the pin's left edge (the bubble runs
 * right), `end` = at its right edge (runs left); right/left — `up` = the
 * corner on the tip line (the bubble runs up), `down` = at the pin's top
 * (runs down). Above comes first because the pin already sits ABOVE its tip,
 * so a bubble over it is the furthest from the content the tip points at;
 * below comes last because it is the side that lands on that content.
 */
const BUBBLE_CANDIDATES: ReadonlyArray<{ side: BubbleSide; along: "start" | "end" | "up" | "down" }> = [
  { side: "above", along: "start" },
  { side: "above", along: "end" },
  { side: "right", along: "up" },
  { side: "right", along: "down" },
  { side: "left", along: "up" },
  { side: "left", along: "down" },
  { side: "below", along: "start" },
  { side: "below", along: "end" },
];

/** `v` clamped into [lo, hi]; an empty range (hi < lo) pins it to `lo`. */
const clampRange = (v: number, lo: number, hi: number): number =>
  hi < lo ? lo : Math.min(hi, Math.max(lo, v));

function overlapArea(a: ScreenRect, b: ScreenRect): number {
  const w = Math.min(a.right, b.right) - Math.max(a.left, b.left);
  const h = Math.min(a.bottom, b.bottom) - Math.max(a.top, b.top);
  return w > 0 && h > 0 ? w * h : 0;
}

/**
 * Where a comment bubble (composer / thread popover) opens: a CORNER of the
 * bubble at its pin, clear of the commented content (the owner's ask,
 * 2026-10-15 — the bubble used to centre on the tip 10px away, which put it
 * on top of the cell below the pin, and a far-off one read as unrelated).
 *
 * 1. Corner placements: of the candidates that fit entirely on screen, the
 *    one covering the least of the target cell/element wins, ties going to
 *    the candidate order above.
 * 2. Fallback, when no corner placement fits (a phone-width board, or a pin
 *    against two edges): each side is slid along the pin into the viewport,
 *    but never so far that the bubble stops covering the pin's whole edge —
 *    it still touches, just not at a corner. Winner: least area off screen
 *    (an input you can't reach is the worst outcome), then least over the
 *    target, then candidate order.
 * A pin panned off screen therefore keeps its bubble beside it — the composer
 * holds a draft, and panning back must find it still at its pin.
 */
export function placeBubble(input: BubblePlacementInput): BubblePlacement {
  const { pin, target, bubble, viewport, gap, margin } = input;
  const w = bubble.width;
  const h = bubble.height;
  const screen: ScreenRect = { left: 0, top: 0, right: viewport.width, bottom: viewport.height };

  // Fallback slide along the pin's edge: into the viewport first, then back
  // to touching — the bubble's edge must span the pin's whole edge.
  const slideX = (x: number) =>
    clampRange(clampRange(x, margin, viewport.width - margin - w), pin.right - w, pin.left);
  const slideY = (y: number) =>
    clampRange(clampRange(y, margin, viewport.height - margin - h), pin.bottom - h, pin.top);

  const pick = (slide: boolean) => {
    let best = {
      placement: { side: "above", left: pin.left, y: pin.top - gap } as BubblePlacement,
      overflow: Infinity,
      overlap: Infinity,
    };
    for (const { side, along } of BUBBLE_CANDIDATES) {
      let left: number;
      let top: number;
      if (side === "above" || side === "below") {
        left = along === "start" ? pin.left : pin.right - w;
        if (slide) left = slideX(left);
        top = side === "above" ? pin.top - gap - h : pin.bottom + gap;
      } else {
        left = side === "right" ? pin.right + gap : pin.left - gap - w;
        top = along === "down" ? pin.top : pin.bottom - h;
        if (slide) top = slideY(top);
      }
      const rect = { left, top, right: left + w, bottom: top + h };
      // Whole px² — fractional camera coordinates leave float dust (1e-11)
      // that would otherwise make a bubble that fits look like it doesn't.
      const overflow = Math.max(0, Math.round(w * h - overlapArea(rect, screen)));
      const overlap = target === null ? 0 : overlapArea(rect, target);
      if (overflow < best.overflow || (overflow === best.overflow && overlap < best.overlap)) {
        best = { placement: { side, left, y: side === "above" ? top + h : top }, overflow, overlap };
      }
    }
    return best;
  };

  const corner = pick(false);
  return corner.overflow === 0 ? corner.placement : pick(true).placement;
}

/* ------------------------------------------------------------------ */
/* Presentation helpers (pure, unit-tested)                            */
/* ------------------------------------------------------------------ */

const MINUTE_MS = 60_000;
const HOUR_MS = 3_600_000;
const DAY_MS = 86_400_000;

/** Compact relative timestamp for comment messages (F-51). */
export function formatRelativeTime(ts: number, now: number): string {
  const diff = Math.max(0, now - ts);
  if (diff < 45_000) return "just now";
  if (diff < HOUR_MS) return `${Math.max(1, Math.floor(diff / MINUTE_MS))}m ago`;
  if (diff < DAY_MS) return `${Math.floor(diff / HOUR_MS)}h ago`;
  if (diff < 7 * DAY_MS) return `${Math.floor(diff / DAY_MS)}d ago`;
  // Older than a week: absolute date (deterministic, locale-independent).
  return new Date(ts).toISOString().slice(0, 10);
}

/**
 * F-54 body segmentation now lives in @kola/shared, next to the tokenizer and
 * the directory it must agree with — the highlight, the autocomplete and the
 * server's notification fan-out all resolve a body through one implementation,
 * so they cannot drift. Re-exported here because this module has always been
 * the comments feature's presentation-helper door.
 */
export { mentionSegments, type BodySegment } from "@kola/shared";
